/**
 * Visible reply and lifecycle activity stream for an expanded clinical note.
 */

import { useState } from "react";
import type { NoteActivityResponse } from "../../../api/clinicalTrialsApiTypes";
import type { RichTextDocument } from "../../../types/notes";
import {
  createEmptyRichText,
  richTextToPlainText,
} from "../../../utils/richText";
import { RichTextContent } from "./RichTextContent";
import { RichTextEditor } from "./RichTextEditor";

interface NoteActivityThreadProps {
  activities: NoteActivityResponse[];
  canReply: boolean;
  isSaving: boolean;
  onReply: (body: RichTextDocument) => Promise<void>;
  onUpdateReply: (
    activityId: string,
    body: RichTextDocument,
  ) => Promise<void>;
  onDeleteReply: (activityId: string) => Promise<void>;
}

/** Format one activity timestamp for the compact panel. */
function formatActivityTime(timestamp: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

/**
 * Render audit activities and a structured rich-text reply composer.
 *
 * @param props - Activities, lifecycle state, and reply callback
 * @returns Note activity thread
 */
export function NoteActivityThread({
  activities,
  canReply,
  isSaving,
  onReply,
  onUpdateReply,
  onDeleteReply,
}: NoteActivityThreadProps) {
  const [reply, setReply] = useState<RichTextDocument>(createEmptyRichText);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState<RichTextDocument>(createEmptyRichText);
  const canSubmit = Boolean(richTextToPlainText(reply).trim());
  return (
    <div className="border-t border-blue-100 bg-uiGray-50 px-3 py-3">
      <div className="space-y-2">
        {activities.map((activity) => (
          <div key={activity.activity_id} className="rounded-md border border-uiGray-200 bg-white px-2.5 py-2 text-xs">
            <div className="mb-1 flex justify-between gap-2 text-primaryGray-8">
              <span className="font-medium text-primaryGray-7">{activity.actor.display_name || activity.actor.email || "Unknown"}</span>
              <time>{formatActivityTime(activity.created_at)}</time>
            </div>
            {activity.activity_type === "reply" && activity.body && !activity.deleted_at ? (
              editingId === activity.activity_id ? (
                <div>
                  <RichTextEditor value={editBody} onChange={setEditBody} placeholder="Reply…" minRows={2} ariaLabel="Edit reply body" />
                  <div className="mt-1 flex justify-end gap-2">
                    <button type="button" onClick={() => setEditingId(null)} className="font-medium">Cancel</button>
                    <button type="button" disabled={isSaving} onClick={async () => { await onUpdateReply(activity.activity_id, editBody); setEditingId(null); }} className="font-semibold text-statusBlue-darkText">Save</button>
                  </div>
                </div>
              ) : (
                <>
                  <RichTextContent document={activity.body} className="leading-5 text-primaryGray-2" />
                  <div className="mt-1 flex gap-2 text-[11px]">
                    <button type="button" onClick={() => { setEditingId(activity.activity_id); setEditBody(activity.body ?? createEmptyRichText()); }} className="font-medium text-statusBlue-darkText">Edit</button>
                    <button type="button" onClick={() => { if (window.confirm("Delete this reply?")) void onDeleteReply(activity.activity_id); }} className="font-medium text-statusRed-text">Delete</button>
                  </div>
                </>
              )
            ) : (
              <p className="capitalize text-primaryGray-8">
                {activity.deleted_at ? "Reply deleted" : activity.activity_type.replaceAll("_", " ")}
              </p>
            )}
          </div>
        ))}
      </div>
      {canReply && (
        <form
          className="mt-3"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!canSubmit) return;
            await onReply(reply);
            setReply(createEmptyRichText());
          }}
        >
          <RichTextEditor value={reply} onChange={setReply} placeholder="Reply…" minRows={2} ariaLabel="Reply body" />
          <div className="mt-2 flex justify-end">
            <button type="submit" disabled={!canSubmit || isSaving} className="btn bg-statusBlue-text px-3 py-1 text-xs text-white disabled:opacity-50">Reply</button>
          </div>
        </form>
      )}
    </div>
  );
}
